/**
 * Standalone meetings (TMMAPP-0159 §D, MM-D-150): a meeting with no virtual account, talk only. The
 * rules the list, the session and the editor share, written once.
 *
 * - **The route:** a meeting's session is `/v/{virtualAccountId}/meetings/{mid}/{occ}`, or
 *   `/me/meetings/{mid}/{occ}` for a standalone one. The two families never cross.
 * - **Who organizes:** the organizer is never an invitee, and a reader is always named (with their id)
 *   in their own invitation (TMMAPI-0188 G11). So on a list row, the viewer organizes a standalone
 *   meeting exactly when no invitee is them.
 * - **Who writes** (served, enforced by tmm-api): the organizer, a subscriber participant, and a guest
 *   participant who is a Manager on a virtual account the organizer is on. The app can't see the
 *   last one exactly, so it offers writes to a guest who manages any virtual account shared with
 *   them; a refusal answers 403 and reads as an error. A Read-only guest reads.
 * - **"With {names}":** a standalone meeting is labelled by its people where a virtual account
 *   meeting names its virtual account.
 */
import type { TFunction } from 'i18next';
import type { Me } from '@/api/me';
import type { Meeting, MeetingCalendarEntry, MeetingHome } from '@/api/meetings';
import { formatList } from '@/i18n/format';
import { personName } from '@/i18n/personName';

/** A meeting occurrence's session page. */
export function sessionPathOf(home: MeetingHome, meetingId: number, occurrenceKey: string): string {
  const key = encodeURIComponent(occurrenceKey);
  return home === null
    ? `/me/meetings/${meetingId}/${key}`
    : `/v/${home}/meetings/${meetingId}/${key}`;
}

/** Whether the viewer organizes this standalone row (never an invitee of their own meeting). */
export function organizesEntry(
  entry: Pick<MeetingCalendarEntry, 'invitees'>,
  myUserId: number | null,
): boolean {
  return myUserId !== null && !entry.invitees.some((person) => person.userId === myUserId);
}

/** Whether the viewer may write in a standalone meeting (notes, items, agenda, start and end). */
export function canWriteStandalone(
  me: Pick<Me, 'tier' | 'virtualAccounts'>,
  organizer: boolean,
): boolean {
  if (organizer || me.tier === 'SUBSCRIBER') return true;
  return me.tier === 'GUEST' && me.virtualAccounts.some((row) => !row.mine && row.canManage);
}

function withNames(names: readonly (string | null)[], t: TFunction, locale: string): string {
  return t('meetings.withNames', {
    names: formatList(
      names.map((name) => personName(name, t)),
      locale,
    ),
  });
}

/**
 * A standalone row's label (§D 24): "With {invitees}" for its organizer, "With {organizer}" for
 * everyone else.
 */
export function entryWithLabel(
  entry: Pick<MeetingCalendarEntry, 'invitees' | 'organizerDisplayName'>,
  myUserId: number | null,
  t: TFunction,
  locale: string,
): string {
  if (organizesEntry(entry, myUserId)) {
    return withNames(
      entry.invitees.map((person) => person.displayName),
      t,
      locale,
    );
  }
  return withNames([entry.organizerDisplayName], t, locale);
}

/** A standalone meeting's people other than the viewer: "With {names}" (the session's chip). */
export function meetingWithLabel(
  meeting: Pick<Meeting, 'organizer' | 'invitees'>,
  myUserId: number | null,
  t: TFunction,
  locale: string,
): string {
  const invited = meeting.invitees.filter((invite) => invite.status !== 'CANCELLED');
  const others = [meeting.organizer, ...invited].filter(
    (person) => myUserId === null || person.userId !== myUserId,
  );
  return withNames(
    others.map((person) => person.displayName),
    t,
    locale,
  );
}
